import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  setGeoMode, setGeoKey, setGeoGender, setGeoAge, drillDistrict, drillMandal, geoBack,
} from '../../../features/ui/uiSlice';
import {
  useGeoMetaQuery, useGeoMapQuery, useGeoDistrictQuery, useGeoMandalQuery,
  useGeoDemographicsQuery, useGeoHotspotsQuery, useGeoHeatmapQuery,
} from '../../../services/api';
import { num } from '../../../utils/format';
import DataCard from '../../../components/common/DataCard';
import AppButton from '../../../components/common/AppButton';
import SegmentedTabs from '../../../components/common/SegmentedTabs';
import QueryState from '../../../components/common/QueryState';
import {
  Shimmer, ShimmerText, ShimmerCard, BarListSkeleton, ControlsSkeleton,
} from '../../../components/common/Shimmer';
import { SectionHeading, FootNote } from '../../../components/common/Bits';
import { PillSelect } from '../../../components/common/FormField';
import GeoBarList from './GeoBarList';
import { ramp, shadeFor, keyColor, metricFor, modeWord, sliceLabel, BLUE } from './geoUtils';

/* ---------- shimmer stand-ins, shaped like the panels they replace ---------- */

/** Bare tile grid — the surrounding DataCard is already on screen. */
function TileGridSkeleton({ tiles = 18, columns = 6 }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 1 }}>
      {Array.from({ length: tiles }, (_, i) => (
        <Shimmer key={i} height={0} radius={12} sx={{ pb: '100%' }} />
      ))}
    </Box>
  );
}

/** Heading, sub-line, then a ranked list. */
function PanelSkeleton({ rows = 8 }) {
  return (
    <>
      <Shimmer width="45%" height={15} />
      <Shimmer width="65%" height={10} sx={{ mt: 1, mb: 2 }} />
      <BarListSkeleton rows={rows} />
    </>
  );
}

/** Age band × two gender bars per row. */
function DemographicsSkeleton({ rows = 4 }) {
  return (
    <>
      <Shimmer width="40%" height={15} />
      <Box sx={{ mt: 2 }}>
        {Array.from({ length: rows }, (_, i) => (
          <Box
            key={i}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '72px 1fr', md: '96px 1fr 1fr' },
              gap: 2,
              alignItems: 'center',
              p: '10px 4px',
              borderTop: '1px solid var(--grid)',
            }}
          >
            <Shimmer height={12} width="70%" />
            <Shimmer height={12} radius={6} />
            <Shimmer height={12} radius={6} sx={{ display: { xs: 'none', md: 'block' } }} />
          </Box>
        ))}
      </Box>
    </>
  );
}

/** One card per condition or test, on the same responsive grid. */
function HotspotsSkeleton({ count = 10 }) {
  return (
    <Grid container spacing={1.5}>
      {Array.from({ length: count }, (_, i) => (
        <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
          <Box
            sx={{
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderLeft: '4px solid var(--border)',
              borderRadius: '12px',
              p: '13px 14px',
              height: '100%',
            }}
          >
            <Shimmer width="60%" height={9} />
            <Shimmer width="45%" height={22} sx={{ mt: 1 }} />
            <Shimmer width="80%" height={11} sx={{ mt: 1 }} />
            <Shimmer width="55%" height={9} sx={{ mt: 1 }} />
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

/** Bare heatmap grid — the DataCard around it is already drawn. */
function HeatGridSkeleton({ rows = 10, cols = 18 }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 620 }}>
      {Array.from({ length: rows + 1 }, (_, r) => (
        <Box key={r} sx={{ display: 'grid', gridTemplateColumns: `120px repeat(${cols}, 1fr)`, gap: '3px' }}>
          <Shimmer height={r === 0 ? 10 : 18} width="80%" radius={4} />
          {Array.from({ length: cols }, (_, c) => (
            <Shimmer key={c} height={r === 0 ? 10 : 18} radius={4} />
          ))}
        </Box>
      ))}
    </Box>
  );
}

/** The whole page, while the vocabulary itself is still loading. */
function GeoPageSkeleton() {
  return (
    <>
      <ControlsSkeleton count={4} />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ShimmerCard>
            <Shimmer width="55%" height={15} />
            <Shimmer width="75%" height={10} sx={{ mt: 1, mb: 2 }} />
            <TileGridSkeleton />
          </ShimmerCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ShimmerCard><PanelSkeleton /></ShimmerCard>
        </Grid>
      </Grid>
      <ShimmerCard sx={{ mb: 2 }}><DemographicsSkeleton /></ShimmerCard>
      <Box sx={{ mb: 2 }}><HotspotsSkeleton count={6} /></Box>
      <ShimmerCard sx={{ overflowX: 'auto' }}><HeatGridSkeleton /></ShimmerCard>
    </>
  );
}

/* ---------- district tile map ---------- */
function GeoMap({ map, selected, onDrill }) {
  const { districts, min, max } = map;
  const maxR = Math.max(...districts.map((d) => d.r));
  const maxC = Math.max(...districts.map((d) => d.c));
  const cell = 76;
  const gap = 8;
  const W = (maxC + 1) * (cell + gap) + gap;
  const H = (maxR + 1) * (cell + gap) + gap;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* fixed aspect ratio, so centre it rather than leaving dead space to one side */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
          {districts.map((d) => {
            const t = shadeFor(d.value, min, max);
            const x = gap + d.c * (cell + gap);
            const y = gap + d.r * (cell + gap);
            const sel = selected === d.district;
            const txt = t > 0.58 ? '#fff' : '#0b0b0b';
            return (
              <g key={d.district} style={{ cursor: 'pointer' }} onClick={() => onDrill(d.district)}>
                <rect
                  x={x} y={y} width={cell} height={cell} rx={12} fill={ramp(t)}
                  stroke={sel ? '#0b0b0b' : 'rgba(0,0,0,.16)'} strokeWidth={sel ? 2.5 : 1}
                  data-t={`${d.district}||${map.key}: ${(100 * d.value).toFixed(0)}% · ${d.patients} patients`}
                />
                <text
                  x={x + cell / 2} y={y + cell / 2 - 4} textAnchor="middle"
                  style={{ fontSize: 10, fontWeight: 600, fill: txt, fontFamily: 'var(--mono)', pointerEvents: 'none' }}
                >
                  {d.short}
                </text>
                <text
                  x={x + cell / 2} y={y + cell / 2 + 12} textAnchor="middle"
                  style={{ fontSize: 12, fontWeight: 700, fill: txt, fontFamily: 'var(--disp)', pointerEvents: 'none' }}
                >
                  {(100 * d.value).toFixed(0)}%
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1.5 }}>
        <span className="lbl">{(100 * min).toFixed(0)}%</span>
        <Box sx={{ flex: 1, height: 10, borderRadius: '5px', background: `linear-gradient(90deg,${BLUE.join(',')})` }} />
        <span className="lbl">{(100 * max).toFixed(0)}%</span>
      </Box>
    </Box>
  );
}

/* ---------- drill-down panel ---------- */
function GeoPanel({ geo, activeKey, scope, mapQuery, tests, dispatch }) {
  const profileLabel = geo.mode === 'test' ? 'All tests' : 'All conditions';

  const districtQuery = useGeoDistrictQuery(
    { district: geo.district, ...scope },
    { skip: !geo.district }
  );
  const mandalQuery = useGeoMandalQuery(
    { mandal: geo.mandal, district: geo.district, ...scope },
    { skip: !geo.mandal }
  );

  // top level — rank the districts the map already returned
  if (!geo.district) {
    return (
      <QueryState query={mapQuery} skeleton={<PanelSkeleton />}>
        {(map) => (
          <>
            <Box sx={{ fontFamily: 'var(--disp)', fontSize: 15, fontWeight: 600 }}>Districts by {map.key}</Box>
            <Box sx={{ fontSize: 12, color: 'var(--muted)', mb: 1.75 }}>
              Ranked {metricFor(geo.mode)} · click to drill into mandals &amp; cities
            </Box>
            <GeoBarList
              items={[...map.districts]
                .sort((a, b) => b.value - a.value)
                .map((d) => ({
                  k: d.district,
                  v: d.value,
                  sub: `${d.patients} pt`,
                  color: 'var(--s1)',
                  onClick: () => dispatch(drillDistrict(d.district)),
                }))}
            />
          </>
        )}
      </QueryState>
    );
  }

  return (
    <QueryState query={districtQuery} skeleton={<PanelSkeleton rows={6} />}>
      {(dist) => (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ fontFamily: 'var(--disp)', fontSize: 15, fontWeight: 600 }}>{dist.district}</Box>
            <AppButton mini onClick={() => dispatch(geoBack())}>← All districts</AppButton>
          </Box>
          <Box sx={{ fontSize: 12, color: 'var(--muted)', mb: 1.5 }}>
            {dist.patients} patients · top {modeWord(geo.mode)}: <b>{dist.top?.key}</b> (
            {(100 * (dist.top?.value || 0)).toFixed(0)}%)
          </Box>

          <Box className="lbl" sx={{ m: '12px 0 6px' }}>Mandals by {activeKey} · click to see cities</Box>
          <GeoBarList
            items={dist.mandals.map((m) => ({
              k: m.mandal,
              v: m.value,
              sub: `${m.patients} pt`,
              color: 'var(--s3)',
              onClick: () => dispatch(drillMandal(m.mandal)),
            }))}
          />

          {geo.mandal ? (
            <QueryState query={mandalQuery} skeleton={<BarListSkeleton rows={4} heading />}>
              {(man) => (
                <>
                  <Box className="lbl" sx={{ m: '14px 0 6px' }}>Cities in {man.mandal} by {activeKey}</Box>
                  <GeoBarList
                    items={man.cities.map((c) => ({ k: c.city, v: c.value, sub: `${c.patients} pt`, color: 'var(--s4)' }))}
                  />
                  <Box className="lbl" sx={{ m: '14px 0 6px' }}>{profileLabel} · {man.mandal}</Box>
                  <GeoBarList items={man.profile.map((p) => ({ k: p.key, v: p.value, color: keyColor(p.key, tests) }))} />
                </>
              )}
            </QueryState>
          ) : (
            <>
              <Box className="lbl" sx={{ m: '14px 0 6px' }}>{profileLabel} · {dist.district}</Box>
              <GeoBarList items={dist.profile.map((p) => ({ k: p.key, v: p.value, color: keyColor(p.key, tests) }))} />
            </>
          )}
        </>
      )}
    </QueryState>
  );
}

/* ---------- age × gender breakdown ---------- */
function GeoDemographics({ geo, activeKey, query }) {
  const Bar = ({ v, p, color, max }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, height: 12, background: 'var(--card-2)', borderRadius: '6px', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', borderRadius: '6px', width: `${Math.max(2, (100 * v) / max)}%`, background: color }} />
      </Box>
      <Box sx={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700, width: 38, textAlign: 'right' }}>
        {(100 * v).toFixed(0)}%
      </Box>
      <Box sx={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)', width: 34, textAlign: 'right' }}>{p}</Box>
    </Box>
  );

  const rowSx = {
    display: 'grid',
    gridTemplateColumns: { xs: '72px 1fr', md: '96px 1fr 1fr' },
    gap: 2,
    alignItems: 'center',
    p: '8px 4px',
    borderTop: '1px solid var(--grid)',
  };
  const hdrSx = {
    fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase',
    letterSpacing: '.05em', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 0.75,
  };

  return (
    <DataCard>
      <QueryState query={query} skeleton={<DemographicsSkeleton />}>
        {(demo) => {
          const max = Math.max(0.0001, ...demo.byAge.flatMap((r) => [r.male.value, r.female.value]));
          const ex = demo.byAge[1];
          return (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ fontFamily: 'var(--disp)', fontSize: 15, fontWeight: 600 }}>
                  Demographic breakdown · {demo.scope}
                </Box>
                <span className="lbl">{activeKey} {metricFor(geo.mode)} · age × gender</span>
              </Box>

              <Box sx={{ ...rowSx, borderTop: 0, pt: '2px' }}>
                <Box sx={{ ...hdrSx, fontSize: 10 }}>Age group</Box>
                <Box sx={hdrSx}>
                  <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', background: 'var(--s1)' }} />
                  Male · avg {(100 * demo.male.value).toFixed(0)}%
                </Box>
                <Box sx={{ ...hdrSx, display: { xs: 'none', md: 'flex' } }}>
                  <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', background: 'var(--s5)' }} />
                  Female · avg {(100 * demo.female.value).toFixed(0)}%
                </Box>
              </Box>

              {demo.byAge.map((r) => (
                <Box key={r.age} sx={rowSx}>
                  <Box sx={{ fontSize: 13, fontWeight: 600 }}>{r.age}</Box>
                  <Bar v={r.male.value} p={r.male.patients} color="var(--s1)" max={max} />
                  <Bar v={r.female.value} p={r.female.patients} color="var(--s5)" max={max} />
                </Box>
              ))}

              <FootNote>
                Bars scaled to the highest cell ({(100 * max).toFixed(0)}%); small number = patients in that cell.
                Read: “Male, {ex.age} in {demo.scope} — {(100 * ex.male.value).toFixed(0)}%{' '}
                {activeKey.toLowerCase()} {geo.mode === 'test' ? 'uptake' : ''}”.
              </FootNote>
            </>
          );
        }}
      </QueryState>
    </DataCard>
  );
}

/* ---------- hotspots ---------- */
function GeoHotspots({ query, tests }) {
  return (
    <QueryState query={query} skeleton={<HotspotsSkeleton />}>
      {(data) => (
        <Grid container spacing={1.5}>
          {data.items.map((i) => (
            <Grid key={i.key} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
              <Box
                sx={{
                  background: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderLeft: `4px solid ${keyColor(i.key, tests)}`,
                  borderRadius: '12px',
                  p: '13px 14px',
                  boxShadow: 'var(--shadow)',
                  height: '100%',
                }}
              >
                <Box sx={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--muted)' }}>
                  {i.key}
                </Box>
                <Box sx={{ fontFamily: 'var(--disp)', fontWeight: 700, fontSize: 24, m: '3px 0' }}>
                  {(100 * i.value).toFixed(0)}%
                </Box>
                <Box sx={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.35 }}>
                  {i.city ? (
                    <>
                      <b>{i.city}</b>
                      <br />
                      {i.mandal}, {i.district}
                    </>
                  ) : (
                    <span style={{ color: 'var(--muted)' }}>No locality meets the patient threshold</span>
                  )}
                </Box>
                <Box sx={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--muted)', mt: 0.75 }}>
                  {i.patients} patients
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </QueryState>
  );
}

/* ---------- key × district heatmap ---------- */
function GeoHeatmap({ query, tests, dispatch }) {
  return (
    <QueryState query={query} skeleton={<HeatGridSkeleton />}>
      {(data) => (
        <table className="heat">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>{data.mode === 'test' ? 'Test' : 'Condition'} \ District</th>
              {data.districts.map((d) => (
                <th key={d.district} className="gh" title={d.district} onClick={() => dispatch(drillDistrict(d.district))}>
                  {d.short}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.key}>
                <td style={{ textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: keyColor(row.key, tests), marginRight: 6 }} />
                  {row.key}
                </td>
                {row.cells.map((c) => {
                  const t = shadeFor(c.value, row.min, row.max);
                  return (
                    <td
                      key={c.district}
                      className="hm"
                      style={{ background: ramp(t), color: t > 0.58 ? '#fff' : '#0b0b0b' }}
                      data-t={`${row.key} · ${c.district}||${(100 * c.value).toFixed(0)}% of ${c.patients} patients`}
                    >
                      {(100 * c.value).toFixed(0)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </QueryState>
  );
}

/* ---------- page ---------- */
export default function GeoTab() {
  const dispatch = useAppDispatch();
  const geo = useAppSelector((s) => s.ui.geo);

  const metaQuery = useGeoMetaQuery();
  const meta = metaQuery.data;
  const keys = meta ? (geo.mode === 'test' ? meta.tests : meta.conditions) : [];
  const tests = meta?.tests || [];
  // `key: null` means "whatever the API considers first" — it echoes back the one it used
  const activeKey = geo.key || keys[0] || '';

  const scope = { mode: geo.mode, key: geo.key || undefined, gender: geo.gender, age: geo.age };
  const ready = { skip: !meta };

  const mapQuery = useGeoMapQuery(scope, ready);
  const demographicsQuery = useGeoDemographicsQuery(
    { district: geo.district || undefined, mandal: geo.mandal || undefined, mode: geo.mode, key: geo.key || undefined },
    ready
  );
  const hotspotsQuery = useGeoHotspotsQuery({ mode: geo.mode, gender: geo.gender, age: geo.age }, ready);
  const heatmapQuery = useGeoHeatmapQuery({ mode: geo.mode, gender: geo.gender, age: geo.age }, ready);

  const filtered = geo.gender !== 'all' || geo.age !== 'all';
  const cap = metricFor(geo.mode);

  return (
    <QueryState query={metaQuery} skeleton={<GeoPageSkeleton />}>
      {(m) => (
        <>
          {/* filter bar on the 12-column grid */}
          <Grid container spacing={1.5} sx={{ alignItems: 'center', mb: 2 }}>
            <Grid size={{ xs: 12, md: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <span className="lbl">Analyse by</span>
                <SegmentedTabs
                  dense
                  value={geo.mode}
                  onChange={(v) => dispatch(setGeoMode(v))}
                  items={[{ value: 'cond', label: 'Condition' }, { value: 'test', label: 'Test' }]}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <PillSelect
                fullWidth
                value={activeKey}
                onChange={(e) => dispatch(setGeoKey(e.target.value))}
                options={keys}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 'auto' }} sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Box sx={{ width: '1px', height: 22, background: 'var(--border)' }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2, lg: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span className="lbl">Gender</span>
                <PillSelect
                  fullWidth
                  value={geo.gender}
                  onChange={(e) => dispatch(setGeoGender(e.target.value))}
                  options={[{ value: 'all', label: 'All' }, ...m.genders]}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2, lg: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span className="lbl">Age</span>
                <PillSelect
                  fullWidth
                  value={geo.age}
                  onChange={(e) => dispatch(setGeoAge(e.target.value))}
                  options={[{ value: 'all', label: 'All' }, ...m.ages]}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 'grow' }}>
              <Box sx={{ color: 'var(--ink-2)', fontSize: 13 }}>
                {mapQuery.data ? `${num(mapQuery.data.totalPatients)} patients` : <ShimmerText chars={12} />} ·{' '}
                {sliceLabel(geo.gender, geo.age)}
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <DataCard
                stretch
                title={`Telangana · ${activeKey} ${cap} by district`}
                subtitle="District tile map (arranged geographically) · click a district to drill in · hover for detail"
                bodySx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <QueryState query={mapQuery} skeleton={<TileGridSkeleton />}>
                  {(map) => (
                    <GeoMap map={map} selected={geo.district} onDrill={(n) => dispatch(drillDistrict(n))} />
                  )}
                </QueryState>
              </DataCard>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <DataCard stretch>
                <GeoPanel
                  geo={geo}
                  activeKey={activeKey}
                  scope={scope}
                  mapQuery={mapQuery}
                  tests={tests}
                  dispatch={dispatch}
                />
              </DataCard>
            </Grid>
          </Grid>

          <SectionHeading>
            Demographic analysis — age group × gender
            {filtered ? ` · (map & hotspots above are filtered to ${sliceLabel(geo.gender, geo.age)})` : ''}
          </SectionHeading>
          <GeoDemographics geo={geo} activeKey={activeKey} query={demographicsQuery} />

          <SectionHeading>
            {geo.mode === 'test'
              ? 'Test hotspots — highest-uptake locality per test'
              : 'Condition hotspots — highest-prevalence locality per condition'}{' '}
            · {sliceLabel(geo.gender, geo.age)}
          </SectionHeading>
          <GeoHotspots query={hotspotsQuery} tests={tests} />

          <SectionHeading>
            {geo.mode === 'test' ? 'Test' : 'Condition'} × district — {cap} heatmap (each row shaded within its own range)
          </SectionHeading>
          <DataCard sx={{ overflowX: 'auto' }}>
            <GeoHeatmap query={heatmapQuery} tests={tests} dispatch={dispatch} />
          </DataCard>

          <FootNote>
            {geo.mode === 'test'
              ? 'Uptake = share of tested patients in that location who received this test.'
              : 'Prevalence = share of tested patients in that location flagged with the condition.'}{' '}
            Figures come from the patient demographics held by the API.
          </FootNote>
        </>
      )}
    </QueryState>
  );
}
